import React, { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card } from '../components/UIComponents';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { AppMark } from '../components/Logo';
import { RegisterRequest } from '../types';
import { useAuth } from '../hooks/useAuth';
import { handleApiError } from '../api/client';
import { useTranslation } from 'react-i18next';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useLanguage } from '../components/LanguageProvider';

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { t } = useTranslation();
  const { language } = useLanguage();

  const [formData, setFormData] = useState<RegisterRequest>({
    name: '',
    email: '',
    password: '',
    language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  });

  useEffect(() => {
    setFormData(prev => ({ ...prev, language }));
  }, [language]);

  useEffect(() => {
    let codeFromUrl = searchParams.get('ref');
    
    // Fallback: Check window.location.search (query before #) for HashRouter compatibility
    if (!codeFromUrl) {
      const browserParams = new URLSearchParams(window.location.search);
      codeFromUrl = browserParams.get('ref');
    }

    if (codeFromUrl) {
      sessionStorage.setItem('omniscribe_referral_code', codeFromUrl);
      setFormData(prev => ({ ...prev, referral_code: codeFromUrl || undefined }));
    } else {
      const storedCode = sessionStorage.getItem('omniscribe_referral_code');
      if (storedCode) {
        setFormData(prev => ({ ...prev, referral_code: storedCode || undefined }));
      }
    }
  }, [searchParams]);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);

  const handleTurnstileTokenChange = useCallback((token: string | null) => {
    setTurnstileToken(token);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password.length < 8) {
      setError(t('auth.passwordRequirements'));
      setLoading(false);
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError(t('auth.verificationRequired'));
      setLoading(false);
      return;
    }

    try {
      await register({ ...formData, language, turnstile_token: turnstileToken || undefined });
      navigate('/dashboard');
    } catch (err: unknown) {
      setError(handleApiError(err));
      setTurnstileResetSignal((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center mb-6">
             <AppMark size={56} />
          </Link>
          <h2 className="h-section">
            {t('auth.createAccount')}
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-zinc-400">
            {t('auth.haveAccount')}{' '}
            <Link to="/login" className="font-medium text-accent hover:underline underline-offset-4">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>

        <Card className="p-8 !bg-card !ring-0 !shadow-none !rounded-[28px]">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
                {t('auth.fullName')}
              </label>
              <div className="mt-1">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="block h-11 w-full rounded-[12px] border border-transparent bg-raised px-3.5 text-[15px] text-ink placeholder:text-ink-3 shadow-[0_0_0_0.5px_rgba(0,0,0,0.06)] focus:border-transparent focus:outline-none focus:ring-[3px] focus:ring-accent/30 transition-shadow dark:bg-white/[0.06]"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
                {t('common.email')}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block h-11 w-full rounded-[12px] border border-transparent bg-raised px-3.5 text-[15px] text-ink placeholder:text-ink-3 shadow-[0_0_0_0.5px_rgba(0,0,0,0.06)] focus:border-transparent focus:outline-none focus:ring-[3px] focus:ring-accent/30 transition-shadow dark:bg-white/[0.06]"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
                {t('common.password')}
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  className="block h-11 w-full rounded-[12px] border border-transparent bg-raised px-3.5 text-[15px] text-ink placeholder:text-ink-3 shadow-[0_0_0_0.5px_rgba(0,0,0,0.06)] focus:border-transparent focus:outline-none focus:ring-[3px] focus:ring-accent/30 transition-shadow dark:bg-white/[0.06] pe-10"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 end-0 px-3 flex items-center text-slate-400 hover:text-slate-600 dark:text-zinc-500 dark:hover:text-zinc-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-zinc-400">
                 {t('auth.passwordRequirements')}
              </p>
            </div>

            <div>
              <label htmlFor="referral_code" className="block text-sm font-medium text-slate-700 dark:text-zinc-300">
                {t('auth.referralCode')}
              </label>
              <div className="mt-1">
                <input
                  id="referral_code"
                  name="referral_code"
                  type="text"
                  placeholder={t('auth.referralCodePlaceholder')}
                  className="block h-11 w-full rounded-[12px] border border-transparent bg-raised px-3.5 text-[15px] text-ink placeholder:text-ink-3 shadow-[0_0_0_0.5px_rgba(0,0,0,0.06)] focus:border-transparent focus:outline-none focus:ring-[3px] focus:ring-accent/30 transition-shadow dark:bg-white/[0.06]"
                  value={formData.referral_code || ''}
                  onChange={(e) => setFormData({...formData, referral_code: e.target.value})}
                />
              </div>
            </div>

            {turnstileSiteKey && (
              <TurnstileWidget
                siteKey={turnstileSiteKey}
                resetSignal={turnstileResetSignal}
                onTokenChange={handleTurnstileTokenChange}
              />
            )}

            <Button type="submit" className="w-full" isLoading={loading}>
              {t('auth.createAccount')}
            </Button>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-line" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-card px-3 text-[13px] text-ink-3">
                    {t('auth.orContinueWith')}
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <GoogleLoginButton mode="register" redirectUrl="/dashboard" />
              </div>
            </div>

            <p className="text-xs text-center text-slate-500 dark:text-zinc-400 mt-4">
              By using OmniScribe, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-indigo-600 dark:hover:text-indigo-400">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="underline hover:text-indigo-600 dark:hover:text-indigo-400">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Register;
